import { userStore } from "@/store/user-store";
import { type ComponentProps, useState } from "react";

export type PrivateAssetImageProps = Omit<ComponentProps<"img">, "src" | "onError"> & {
  src: string;
};

const withRetryParam = (src: string): string => {
  const url = new URL(src, window.location.href);
  url.searchParams.set("assetAccessRetry", Date.now().toString());
  return url.toString();
};

export const PrivateAssetImage = ({ src, alt, ...props }: PrivateAssetImageProps) => {
  const [retry, setRetry] = useState<{ src: string; retrySrc: string } | null>(null);
  const hasRetried = retry?.src === src;

  const handleError = () => {
    if (hasRetried) return;

    // A failed load is usually an expired CloudFront cookie; the URL stays the same, so the browser would not retry it
    // on its own. Renewal errors are ignored here because the app-level renewal already ends the session on failure.
    void userStore
      .getState()
      .renewExpiringAssetAccess()
      .catch(() => undefined)
      .then(() => setRetry({ src, retrySrc: withRetryParam(src) }));
  };

  return <img src={hasRetried ? retry.retrySrc : src} alt={alt} onError={handleError} {...props} />;
};
